import type { FC } from "react";
import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import type { TooltipPositionerCallbackFunction } from "highcharts/highcharts";

import type { VennDiagramData } from "@similarweb/pro-logic-components";
import { LEGEND_POSITIONS, VennDiagram } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { CircleSwitcherItem, PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import durationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import type { SourceType } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    KEYWORDS_GAP_GRAPH_ENDPOINT,
    KEYWORDS_GAP_VALUE_KEY,
    MetricTypes,
    metricTypeSwitcherItems,
    VOLUME_GAP_VALUE_KEY,
} from "../const";
import { StyledSwitcher } from "../StyledComponents";
import type { IKeywordsGapServerResponse } from "../Types";

import { StyledWidgetContainer, StyledWidgetFooterContainer } from "./StyledComponents";
import {
    combineSiteFilters,
    getConcatenatedSiteKeys,
    getNoDataTitle,
    getSiteKey,
    legendDataAdapter,
    transformKeywordsGapData,
} from "./utils";
import { createVennTooltipFormatter } from "./VennTooltip";

const VENN_TOOLTIP_OFFSET = 20;

const graphConfig = {
    plotOptions: {
        venn: {
            states: {
                hover: {
                    enabled: true,
                    opacity: 1,
                },
                inactive: {
                    enabled: true,
                    opacity: 0.1,
                },
            },
        },
    },
    tooltip: {
        hideDelay: 0,
        positioner: function (_, labelHeight, point) {
            const x = point.plotX + this.chart.plotLeft;
            const y = point.plotY + this.chart.plotTop - labelHeight - VENN_TOOLTIP_OFFSET;
            return { x, y };
        } as TooltipPositionerCallbackFunction,
    },
    chart: {
        height: 308,
        marginBottom: 10,
        marginTop: 10,
        spacingBottom: 0,
        spacingTop: 0,
    },
};

export const KeywordsGap: FC = () => {
    const [selectedMetricIndex, setSelectedMetricIndex] = useState<MetricTypes>(MetricTypes.KEYWORDS);
    const { key, country, webSource, duration, pageFilter, competitorsFilter, vennDiagramSourceType } =
        useCurrentStateAndParams().params;

    const keys = getConcatenatedSiteKeys(key, competitorsFilter);
    const keysString = keys.join(",");
    const combinedPageFilter = combineSiteFilters(pageFilter, competitorsFilter);
    const combinedPageFilterString = JSON.stringify(combinedPageFilter);
    const sourceType = SourceTypeMap[vennDiagramSourceType as SourceType];
    const { from, to, isWindow } = durationService.getDurationData(duration).forAPI;

    const apiParams = {
        from,
        to,
        keys: keysString,
        country,
        isWindow,
        pageFilterJson: combinedPageFilterString,
        sourceType,
        webSource,
    };

    const {
        data = [],
        isLoading,
        isError,
    } = useQuery({
        queryKey: [KEYWORDS_GAP_GRAPH_ENDPOINT, apiParams],
        queryFn: async () =>
            DefaultFetchService.getInstance().get<IKeywordsGapServerResponse>(KEYWORDS_GAP_GRAPH_ENDPOINT, apiParams),
        select: (response) => {
            const siteKeys = combinedPageFilter.map(getSiteKey);
            return transformKeywordsGapData(response, siteKeys);
        },
    });

    const onSwitcherItemClick = (index: number) => {
        setSelectedMetricIndex(index);
    };

    const footerCtaLinkParams = {
        country,
        duration,
        webSource,
        key: keysString,
        pageFilter: combinedPageFilterString,
        selectedPageTab: vennDiagramSourceType,
    };

    const footerCtaLink = proNavigator.href("organicsearch_websites_v2_traffic_keywords", footerCtaLinkParams);

    const valueKey = selectedMetricIndex === MetricTypes.KEYWORDS ? KEYWORDS_GAP_VALUE_KEY : VOLUME_GAP_VALUE_KEY;

    const dataToDisplay: VennDiagramData = [
        {
            data: data.map((item) => ({
                ...item,
                value: item[valueKey],
            })),
        },
    ];

    const noDataTitle = getNoDataTitle(!!safeJsonParse(competitorsFilter, []).length, isError);

    return (
        <StyledWidgetContainer
            title={translate("wa.traffic.sources.search.seo.overview.keywords.gap.title")}
            withBorder={true}
            noData={isLoading ? false : isError || !data?.length}
            noDataTitle={noDataTitle.noDataTitle}
            noDataSubtitle={noDataTitle.noDataSubtitle}
            footerButtonsConfig={[
                {
                    type: buttonTypes.FLAT,
                    text: translate("wa.traffic.sources.search.seo.overview.keywords.gap.footer.cta"),
                    link: footerCtaLink,
                    onClick: () => {
                        allTrackers.dispatchTrackEvent(
                            "Internal Link",
                            "click",
                            "Competitive Opportunities/Keyword Gap",
                        );
                    },
                },
            ]}
            footerWrapperComponent={StyledWidgetFooterContainer}
        >
            <Flex
                flexDirection="column"
                height="100%"
                width="100%"
                padding="24px 24px 0"
                boxSizing="border-box"
                overflow="hidden"
                position="relative"
            >
                <Flex
                    gap="16px"
                    alignItems="center"
                    position="absolute"
                    zIndex={2}
                    top="24px"
                    right="24px"
                >
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("wa.traffic.sources.search.seo.overview.keywords.gap.metric_switcher")}
                    </Text>
                    <StyledSwitcher
                        selectedIndex={selectedMetricIndex}
                        onItemClick={onSwitcherItemClick}
                        customClass="CircleSwitcher"
                    >
                        {metricTypeSwitcherItems.map((item) =>
                            isLoading ? (
                                <PixelPlaceholderLoader
                                    width="110px"
                                    height="36px"
                                />
                            ) : (
                                <CircleSwitcherItem
                                    key={item.id}
                                    disabled={isError}
                                >
                                    {translate(item.textKey)}
                                </CircleSwitcherItem>
                            ),
                        )}
                    </StyledSwitcher>
                </Flex>
                {isLoading || !data.length ? (
                    <Flex
                        flex={1}
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                    >
                        <PixelPlaceholderLoader
                            width="100%"
                            height="235px"
                        />
                    </Flex>
                ) : (
                    <VennDiagram
                        key={`${selectedMetricIndex}-${isLoading}-${vennDiagramSourceType}`}
                        data={dataToDisplay}
                        graphConfig={graphConfig}
                        withoutTooltipsPadding={false}
                        legendPosition={LEGEND_POSITIONS.RIGHT}
                        legendsMinWidth={200}
                        legendsMaxWidth={240}
                        legendGraphGap={1}
                        legendDataAdapter={legendDataAdapter}
                        tooltipFormatter={createVennTooltipFormatter(data, selectedMetricIndex)}
                    />
                )}
            </Flex>
        </StyledWidgetContainer>
    );
};
